/* ──────────────────────────────────────────────────────────
   Dashboard app registry — maps each AppName to
   chart type, title, and data-field descriptors.
   ────────────────────────────────────────────────────────── */

export type ChartKind =
  | "donut"
  | "bar"
  | "dualBar"
  | "stackedBar"
  | "line"
  | "area"
  | "datatable"
  | "metrics"
  | "image"
  | "stub";

export interface DonutField {
  value: string;
  color: string;
  label: string;
  tooltip?: string;
  /** Legend item format function. Receives a data row. */
  legendFn?: (r: Record<string, unknown>) => string;
}

export interface BarField {
  value: string;
  label: string;
  color?: string;
}

export interface DualBarField extends BarField {
  scoreField: string; // "TOP" or "BOTTOM"
}

export interface StackedBarSeries {
  field: string;
  color: string;
  label: string;
}

export interface StackedBarField {
  labelField: string;
  series: StackedBarSeries[];
}

export interface LineSeries {
  field: string;
  color: string;
  label: string;
  fill?: boolean; // area chart
}

export interface LineField {
  labelField: string;
  series: LineSeries[];
  showLabels?: boolean;
  /** Render each series as its own chart (stacked vertically). */
  splitSeries?: boolean;
}

export interface TableColumn {
  field: string;
  header: string;
}

export interface MetricRow {
  typeField: string;
  valueFields: { field: string; label: string }[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>;

export interface AppConfig {
  title: string;
  kind: ChartKind;
  donut?: DonutField;
  bar?: BarField;
  dualBar?: DualBarField;
  stackedBar?: StackedBarField;
  line?: LineField;
  table?: TableColumn[];
  metric?: MetricRow;
  imageSrc?: string;
  /** Rich tooltip for the whole data point (row). Shown on hover over chart elements. */
  tooltipFn?: (r: Row) => string;
  /** Static tooltip shown on specific metric labels. */
  metricTooltips?: Record<string, string>;
}

// ── Registry ─────────────────────────────────────────────

export const appRegistry: Record<string, AppConfig> = {
  // ─── Donut charts ──────────────────────────────
  KioskStatusApp: {
    title: "Kiosk Status",
    kind: "donut",
    donut: {
      value: "Value", color: "Color", label: "Status", tooltip: "AffectedKiosks",
      legendFn: (r) => `${r.Value}% ${r.Status}`,
    },
    tooltipFn: (r) => String(r.AffectedKiosks ?? ""),
  },
  ProductSalesApp: {
    title: "Top product sales, last month",
    kind: "donut",
    donut: {
      value: "Value", color: "Color", label: "Product", tooltip: "Count",
      legendFn: (r) => `${r.Value}% ${r.Product} (${r.Count})`,
    },
    tooltipFn: (r) => `${r.Value}% ${r.Product}`,
  },
  ProductSalesPerDayApp: {
    title: "Product sales last 24 hours",
    kind: "donut",
    donut: {
      value: "Value", color: "Color", label: "Product", tooltip: "Count",
      legendFn: (r) => `${r.Value}% ${r.Product} (${r.Count})`,
    },
    tooltipFn: (r) => `${r.Value}% ${r.Product}`,
  },
  DiscStatus: {
    title: "Network Disc Status",
    kind: "donut",
    donut: {
      value: "Value", color: "Color", label: "Status",
      legendFn: (r) => `${r.Status}: ${r.Value}`,
    },
    tooltipFn: (r) => String(r.Value ?? ""),
  },
  LovaPercentageApp: {
    title: "Lova Overview this week",
    kind: "donut",
    donut: {
      value: "Count", color: "Color", label: "Type", tooltip: "Percentage",
      legendFn: (r) => `${r.Percentage}% ${r.Type}`,
    },
    tooltipFn: (r) => `${r.Count} transactions`,
  },
  LovaReviewApp: {
    title: "Lova Review",
    kind: "donut",
    donut: {
      value: "NumRatings", color: "Color", label: "RatingName", tooltip: "Rating",
      legendFn: (r) => `${r.RatingName} Count: ${r.NumRatings}`,
    },
    tooltipFn: (r) => String(r.NumRatings ?? ""),
  },
  iiOrderStatusApp: {
    title: "Order Status",
    kind: "donut",
    donut: {
      value: "StatusCount", color: "Color", label: "Status",
      legendFn: (r) => `${r.StatusCount} ${r.Status}`,
    },
    tooltipFn: (r) => String(r.StatusCount ?? ""),
  },
  iiTestStatusApp: {
    title: "Test Status",
    kind: "donut",
    donut: {
      value: "StatusCount", color: "Color", label: "Status",
      legendFn: (r) => `${r.StatusCount} ${r.Status}`,
    },
    tooltipFn: (r) => String(r.StatusCount ?? ""),
  },
  MerchandiserDiscrepancyApp: {
    title: "Merchandiser Discrepancies last 4 weeks",
    kind: "donut",
    donut: {
      value: "TotalCount", color: "Color", label: "sText", tooltip: "DiscrepancyPercent",
      legendFn: (r) => `${r.TotalCount} ${r.sText}`,
    },
    tooltipFn: (r) => `${r.sText}: ${r.TotalCount}, Discrepancy %: ${r.DiscrepancyPercent}\nData excludes last 7 days to give time for stocking`,
  },
  RentalRate: {
    title: "User Rental Rate",
    kind: "donut",
    donut: {
      value: "UserCount", color: "Color", label: "Rate",
      legendFn: (r) => `Rental Rate ${r.Rate}: ${r.UserCount}%`,
    },
    tooltipFn: (r) => `${r.UserCount}%`,
  },
  StockingStatsApp: {
    title: "Stock Status",
    kind: "donut",
    donut: {
      value: "StockCount", color: "Color", label: "StockStatus",
      legendFn: (r) => `${r.StockCount} ${r.StockStatus} items`,
    },
    tooltipFn: (r) => String(r.StockStatus ?? ""),
  },
  TicketStatus: {
    title: "Network Ticket Status",
    kind: "donut",
    donut: {
      value: "Count", color: "Color", label: "Status",
      legendFn: (r) => `${r.Status}: ${r.Count}`,
    },
    tooltipFn: (r) => String(r.Count ?? ""),
  },

  // ─── Bar charts ────────────────────────────────
  TransactionRateApp: {
    title: "Transaction rate, last 24 hours",
    kind: "bar",
    bar: { value: "TransactionCount", label: "Hour" },
    tooltipFn: (r) => String(r.TransactionCount ?? ""),
  },
  TopProductApp: {
    title: "Top 10 Products today",
    kind: "bar",
    bar: { value: "RentalCount", label: "Title" },
    tooltipFn: (r) => `Title: ${r.Title}`,
  },

  // ─── Dual bar (top + bottom) ──────────────────
  TopBottomKioskApp: {
    title: "Top and Bottom 15 kiosks yesterday",
    kind: "dualBar",
    dualBar: { value: "TransactionCount", label: "KioskID", scoreField: "Score" },
    tooltipFn: (r) => String(r.TransactionCount ?? ""),
  },
  NewProductKiosk: {
    title: "New Product Status (Top/Bottom 15 Kiosks)",
    kind: "dualBar",
    dualBar: { value: "OutPercent", label: "KioskID", scoreField: "Score" },
    tooltipFn: (r) =>
      `KioskID: ${r.KioskID}\nOut %: ${r.OutPercent}\n\nNew product count: ${r.ProductCount}\nIN: ${r.DiscsIN}\nOUT: ${r.DiscsOUT}\nSOLD: ${r.DiscsSOLD}\nRESERVED: ${r.DiscsRESERVED}\nBAD: ${r.DiscsBAD}`,
  },

  // ─── Stacked bar ──────────────────────────────
  ICMetricApp: {
    title: "Instant Coupon Metrics / WEEK (last 20)",
    kind: "stackedBar",
    stackedBar: {
      labelField: "RateWeek",
      series: [
        { field: "AtKiosk", color: "#a7ee70", label: "At Kiosk" },
        { field: "ByEmail", color: "#ffa400", label: "By Email" },
        { field: "NoneSelected", color: "#ffe200", label: "None Selected" },
        { field: "Skipped", color: "#ff0000", label: "Skipped" },
      ],
    },
    tooltipFn: (r) =>
      `Week: ${r.RateWeek}\n\nSelected at Kiosk: ${r.AtKiosk}\nChoose by Email: ${r.ByEmail}\nNo coupon selected: ${r.NoneSelected}\nSkipped coupon selection: ${r.Skipped}\nAvg extra time at Delivery page: ${r.ICAvgExtraTime}s`,
  },
  iiDeliveredStatusApp: {
    title: "Yearly Deliveries",
    kind: "stackedBar",
    stackedBar: {
      labelField: "Year",
      series: [{ field: "ProductCount", color: "#3dbeab", label: "Products" }],
    },
    tooltipFn: (r) => `Year: ${r.Year}\nProducts: ${r.ProductCount}`,
  },
  KioskDiscIssue: {
    title: "Kiosk Issue Discs",
    kind: "stackedBar",
    stackedBar: {
      labelField: "KioskID",
      series: [
        { field: "IssueDiscs", color: "#a7ee70", label: "Issue" },
        { field: "UnreadableDiscs", color: "#ffa400", label: "Unreadable" },
        { field: "BadDiscs", color: "#ed3d3d", label: "Bad" },
        { field: "MissingProductId", color: "#e787e1", label: "Missing Product" },
        { field: "UnknownSlots", color: "#c8c8c8", label: "Unknown" },
      ],
    },
    tooltipFn: (r) =>
      `KioskID: ${r.KioskID}\nIssue discs: ${r.IssueDiscs}\nUnreadable: ${r.UnreadableDiscs}\nBad: ${r.BadDiscs}\nMissing Product: ${r.MissingProductId}\nUnknown: ${r.UnknownSlots}\n\nInfo from: ${r.LastUpdated}`,
  },
  NewProductStatusApp: {
    title: "New Product Statuses",
    kind: "stackedBar",
    stackedBar: {
      labelField: "TitleShort",
      series: [
        { field: "DiscsIN", color: "#ffa400", label: "IN" },
        { field: "DiscsOUT", color: "#a7ee70", label: "OUT" },
        { field: "DiscsBAD", color: "#ed3d3d", label: "BAD" },
        { field: "DiscsSOLD", color: "#59d0f3", label: "SOLD" },
        { field: "DiscsRESERVED", color: "#e787e1", label: "RESERVED" },
        { field: "DiscsUNK", color: "#c8c8c8", label: "UNKNOWN" },
      ],
    },
    tooltipFn: (r) =>
      `Title: ${r.Title}\nProductID: ${r.ProductID}\nStreet date: ${r.StreetDate}\nDiscs in system: ${r.DiscCount}\n\nIN: ${r.DiscsIN}\nOUT: ${r.DiscsOUT}\nBAD: ${r.DiscsBAD}\nSOLD: ${r.DiscsSOLD}\nRESERVED: ${r.DiscsRESERVED}\nUNKNOWN: ${r.DiscsUNK}`,
  },
  RentalRateMetrics: {
    title: "Rental Rate Metrics / WEEK (last 20)",
    kind: "stackedBar",
    stackedBar: {
      labelField: "Week",
      series: [
        { field: "UsedPromo", color: "#a7ee70", label: "Used Promo" },
        { field: "No", color: "#ffa400", label: "No" },
      ],
    },
    tooltipFn: (r) =>
      `Week: ${r.Week}\nYES: ${r.Yes}\nYES & USED promotion: ${r.UsedPromo}\nNO: ${r.No}\n\nPercent YES clicks: ${r.pYes}\nPercent USED promotion: ${r.pUsed}`,
  },

  // ─── Line charts ──────────────────────────────
  TransDailyStatsApp: {
    title: "Daily Transaction Stats",
    kind: "line",
    line: {
      labelField: "GroupDay",
      showLabels: false,
      series: [
        { field: "TransCount", color: "#27ae60", label: "Transactions" },
        { field: "MultiCount", color: "#2e86c1", label: "Multi-disc" },
        { field: "AppCount", color: "#f1c40f", label: "App" },
      ],
    },
    tooltipFn: (r) =>
      `Date: ${r.GroupDate}\nTransaction count: ${r.TransCount}\nMulti-Transactions: ${r.MultiCount} (${r.MultiRate}%)\nApp-Transactions: ${r.AppCount} (${r.AppRate}%)`,
  },
  TransRevenueStatsApp: {
    title: "Daily Revenue Stats",
    kind: "line",
    line: {
      labelField: "GroupDay",
      showLabels: false,
      splitSeries: true,
      series: [
        { field: "TransRevenue", color: "#27ae60", label: "Total transaction revenue" },
        { field: "TransAverage", color: "#2e86c1", label: "Average transaction revenue" },
      ],
    },
    tooltipFn: (r) =>
      `Date: ${r.GroupDate}\nTotal Revenue: ${r.TransRevenue}\nAverage transaction revenue: ${r.TransAverage}`,
  },

  // ─── Area charts ──────────────────────────────
  LovaUserApp: {
    title: "Lova user stats last 2 weeks",
    kind: "area",
    line: {
      labelField: "Dates",
      series: [
        { field: "NewUsers", color: "#58dccd", label: "New Users", fill: true },
        { field: "CardCount", color: "#36abee", label: "Cards", fill: true },
      ],
    },
    tooltipFn: (r) => `New users: ${r.NewUsers}\nAdded cards: ${r.CardCount}\nDate: ${r.Dates}`,
  },
  RentguardWaveApp: {
    title: "Rentguard Processing",
    kind: "area",
    line: {
      labelField: "dWhen",
      series: [
        { field: "PaymentCount", color: "#58dccd", label: "Initial", fill: true },
        { field: "LatePaymentCount", color: "#36abee", label: "Late", fill: true },
      ],
    },
    tooltipFn: (r) => `Initial Count: ${r.PaymentCount}\nLate Count: ${r.LatePaymentCount}\nDate: ${r.dWhen}`,
  },

  // ─── Datatable ────────────────────────────────
  GlobalAnnouncementApp: {
    title: "Announcements by Instant Systems",
    kind: "datatable",
    table: [
      { field: "Header", header: "Announcement" },
      { field: "Read", header: "Read" },
    ],
  },
  MerchActivityApp: {
    title: "Last Merchandiser activity",
    kind: "datatable",
    table: [
      { field: "KioskID", header: "Kiosk" },
      { field: "Merchandiser", header: "Merchandiser" },
      { field: "LastAction", header: "Last Action" },
    ],
  },
  Notifications: {
    title: "Notifications",
    kind: "stub",
  },
  OpenTickets: {
    title: "Open Tickets",
    kind: "stub",
  },
  AddTicket: {
    title: "Add Ticket",
    kind: "stub",
  },

  // ─── Metrics / Labels ─────────────────────────
  SessionStats: {
    title: "Customer Session Status",
    kind: "metrics",
    metric: {
      typeField: "SessionType",
      valueFields: [
        { field: "Started", label: "Started" },
        { field: "Completed", label: "Completed" },
        { field: "Aborted", label: "Aborted" },
      ],
    },
    metricTooltips: { Completed: "Completed means sessions resulting in delivery" },
  },
  LovaCountApp: {
    title: "Lova Transaction Count",
    kind: "metrics",
    metric: {
      typeField: "LovaPeriod",
      valueFields: [{ field: "LovaPayments", label: "Payments" }],
    },
    metricTooltips: { "Last 7 days": "Count from last 7 days", "Last 31 days": "Count from last 31 days" },
  },

  // ─── Demo / Image apps ────────────────────────
  DemoProductNotificationsApp: {
    title: "Product Notifications (Demo)",
    kind: "image",
  },
  DemoRevenueApp: {
    title: "Revenue (Demo)",
    kind: "image",
  },
  DemoSalesResultsApp: {
    title: "Sales Results (Demo)",
    kind: "image",
  },
  DemoTopProductsApp: {
    title: "Top Products (Demo)",
    kind: "image",
  },
  DemoTransactionRateApp: {
    title: "Transaction Rate (Demo)",
    kind: "image",
  },
};
